import api from "@/api/api";
import type { User } from "@/types/user";
import { setStorageValue } from "@/utils/storage";
import cacheService from "./cache.service";

interface ProfileUpdateData {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal?: string;
  contact_nom?: string;
  contact_prenom?: string;
  contact_email?: string;
  contact_phone?: string;
  website?: string;
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  avatar?: string;
}

interface ProfileResponse {
  message: string;
  user: User;
}

export const userService = {
  async getAllUsers(): Promise<User[]> {
    const cachedData = cacheService.get<User[]>("users");
    if (cachedData) return cachedData;

    const response = await api.get<User[]>("/api/users");
    const result = response.data;
    cacheService.set("users", result, 60);
    return result;
  },

  async getUserById(id: string): Promise<User> {
    const cacheKey = `user-${id}`;
    const cachedData = cacheService.get<User>(cacheKey);
    if (cachedData) return cachedData;

    const response = await api.get<User>(`/api/user/${id}`);
    const result = response.data;
    cacheService.set(cacheKey, result, 60);
    return result;
  },

  async updateProfile(
    profileData: ProfileUpdateData
  ): Promise<ProfileResponse> {
    try {
      if (!profileData.name || !profileData.email) {
        throw new Error("Le nom et l'email sont requis");
      }

      const profileDataObj = JSON.parse(JSON.stringify(profileData));

      const dataToSend = {
        name: profileDataObj.name,
        email: profileDataObj.email,
        telephone: profileDataObj.phone,
        address: profileDataObj.address,
        city: profileDataObj.city,
        postal_code: profileDataObj.postal,
        contact_lastname: profileDataObj.contact_nom,
        contact_firstname: profileDataObj.contact_prenom,
        contact_email: profileDataObj.contact_email,
        contact_telephone: profileDataObj.contact_phone,
        website: profileDataObj.website,
        facebook: profileDataObj.facebook,
        instagram: profileDataObj.instagram,
        linkedin: profileDataObj.linkedin,
      };

      const token = localStorage.getItem("auth_token");
      const csrfToken = document.cookie
        .split("; ")
        .find((row) => row.startsWith("XSRF-TOKEN="))
        ?.split("=")[1];

      const headers: Record<string, string> = {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      if (csrfToken) {
        headers["X-XSRF-TOKEN"] = decodeURIComponent(csrfToken);
      }

      const response = await api.patch("/profile", dataToSend);

      if (response.status !== 200) {
        const errorData = response.data;
        throw new Error(
          `Erreur ${response.status}: ${
            errorData.message || "Erreur lors de la mise à jour du profil"
          }`
        );
      }

      const data = response.data;

      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      if (userData.id) {
        cacheService.remove(`user-${userData.id}`);
      }
      cacheService.remove("users");

      const updatedUserData = {
        ...userData,
        name: profileData.name,
        email: profileData.email,
        user_details: {
          ...userData.user_details,
          name: profileData.name,
          email: profileData.email,
          telephone: profileData.phone,
          address: profileData.address,
          city: profileData.city,
          postal_code: profileData.postal,
          contact_lastname: profileData.contact_nom,
          contact_firstname: profileData.contact_prenom,
          contact_email: profileData.contact_email,
          contact_telephone: profileData.contact_phone,
          website: profileData.website,
          facebook: profileData.facebook,
          instagram: profileData.instagram,
          linkedin: profileData.linkedin,
          image: profileData.avatar || userData.user_details?.image,
        },
      };

      setStorageValue('user_data', updatedUserData);

      return data;
    } catch (error) {
      if (error instanceof Error) {
        console.error(
          "Erreur lors de la mise à jour du profil:",
          error.message
        );
        throw error;
      }
      throw new Error("Une erreur inconnue s'est produite");
    }
  },

  async checkTermsAcceptance(): Promise<{ accepted: boolean }> {
    try {
      const response = await api.get("/terms-acceptance");
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la vérification de l'acceptation des termes:",
        error
      );
      throw error;
    }
  },

  async acceptTerms(): Promise<{ success: boolean; accepted_at: string }> {
    try {
      const response = await api.post("/terms-acceptance");
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'acceptation des termes:", error);
      throw error;
    }
  },
};
